import { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';

export interface CountdownFlipProps {
  targetIso: string; // date (+ optional time) to count down to
  className?: string;
  labelColor?: string;
  digitColor?: string;
}

interface TimeParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function computeParts(targetMs: number): TimeParts {
  const diff = Math.max(0, targetMs - Date.now());
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function FlipDigit({ value, shouldReduceMotion }: { value: string; shouldReduceMotion: boolean | null }) {
  return (
    <div className="relative w-full h-full overflow-hidden">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={value}
          initial={shouldReduceMotion ? false : { rotateX: -90, opacity: 0 }}
          animate={{ rotateX: 0, opacity: 1 }}
          exit={shouldReduceMotion ? undefined : { rotateX: 90, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="absolute inset-0 flex items-center justify-center"
          style={{ transformStyle: 'preserve-3d' }}
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

/**
 * Flip-clock style countdown to a target date/time — used for occasion moments
 * where the "wow factor" is a live ticking clock (e.g. birthday countdown,
 * baby-shower due-date announcement) rather than a static date string.
 */
export function CountdownFlip({ targetIso, className, labelColor, digitColor }: CountdownFlipProps) {
  const shouldReduceMotion = useReducedMotion();
  const targetMs = new Date(targetIso).getTime();
  const [parts, setParts] = useState<TimeParts>(() => computeParts(targetMs));

  useEffect(() => {
    if (Number.isNaN(targetMs)) return;
    const id = setInterval(() => setParts(computeParts(targetMs)), 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  if (Number.isNaN(targetMs)) return null;

  const units: Array<[string, number]> = [
    ['Days', parts.days],
    ['Hours', parts.hours],
    ['Min', parts.minutes],
    ['Sec', parts.seconds],
  ];

  return (
    <div className={cn('flex items-center justify-center gap-3 md:gap-5', className)}>
      {units.map(([label, val]) => (
        <div key={label} className="flex flex-col items-center">
          <div
            className="w-14 h-16 md:w-20 md:h-24 rounded-xl backdrop-blur-md flex items-center justify-center text-2xl md:text-4xl font-bold shadow-lg border border-white/10"
            style={{ backgroundColor: 'rgba(0,0,0,0.35)', color: digitColor ?? '#fff' }}
          >
            <FlipDigit value={String(val).padStart(2, '0')} shouldReduceMotion={shouldReduceMotion} />
          </div>
          <span
            className="mt-2 text-[10px] md:text-xs uppercase tracking-widest opacity-75"
            style={{ color: labelColor ?? '#fff' }}
          >
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}
